/**
 * Pulizia completa dei dati del portale nel browser, usata dal pulsante "Esci".
 *
 * Non si limita alle chiavi note (JWT, refresh token, utente oidc-client-ts): azzera tutto ciò
 * che l'origine del portale può aver lasciato nel browser, così un PC condiviso non conserva
 * nulla della sessione:
 *  - cache in memoria di TanStack Query (dati già scaricati);
 *  - localStorage e sessionStorage (anche le preferenze UI, es. guide PageHelp chiuse);
 *  - Cache API, database IndexedDB e service worker dell'origine;
 *  - cookie leggibili da JS (il portale non ne usa: difesa in profondità);
 *  - `POST /auth/logout` sul product-service, che risponde con `Clear-Site-Data` e fa
 *    svuotare al browser anche la cache HTTP (endpoint presente solo col login Entra/broker:
 *    altrove 404, ignorato).
 *
 * Best-effort: ogni passo ignora i propri errori (storage bloccato, API assenti). Il chiamante
 * ricarica poi la pagina (reloadToLogin) per buttare anche lo stato in memoria dei moduli JS.
 * La sessione Microsoft dell'utente (cookie di login.microsoftonline.com) NON è toccata: sta su
 * un'altra origine e chiuderla disconnetterebbe l'utente da tutte le app Microsoft.
 */
import { SERVICE_URLS } from '../api/client.js';
import { queryClient } from '../api/queryClient.js';

/** Cookie dell'origine visibili da JS, scaduti su path `/` (gli HttpOnly non sono raggiungibili). */
function expireCookies(): void {
  for (const part of document.cookie.split(';')) {
    const name = part.split('=')[0]?.trim();
    if (name) document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  }
}

async function ignoreErrors(step: () => unknown): Promise<void> {
  try {
    await step();
  } catch {
    /* best-effort: API non disponibile o storage bloccato */
  }
}

export async function wipeBrowserData(fetchFn: typeof fetch = globalThis.fetch): Promise<void> {
  queryClient.clear();
  await ignoreErrors(() => window.localStorage.clear());
  await ignoreErrors(() => window.sessionStorage.clear());
  await ignoreErrors(expireCookies);
  await ignoreErrors(async () => {
    if (!('caches' in window)) return;
    for (const key of await caches.keys()) await caches.delete(key);
  });
  await ignoreErrors(async () => {
    // indexedDB.databases() manca su browser datati: lì resta il Clear-Site-Data del server.
    for (const db of (await indexedDB.databases?.()) ?? []) {
      if (db.name) indexedDB.deleteDatabase(db.name);
    }
  });
  await ignoreErrors(async () => {
    for (const reg of (await navigator.serviceWorker?.getRegistrations()) ?? []) {
      await reg.unregister();
    }
  });
  await ignoreErrors(() =>
    fetchFn(`${SERVICE_URLS.product}/auth/logout`, {
      method: 'POST',
      cache: 'no-store',
      credentials: 'same-origin',
    }),
  );
}

/** Navigazione completa (non SPA) su /login: scarta lo stato in memoria di React e dei moduli. */
export function reloadToLogin(): void {
  window.location.replace('/login');
}
